/**
 * BaseSimulationEngine.ts
 *
 * Abstract base class for all portfolio simulation strategies
 * Eliminates code duplication and provides template method pattern
 * Contains all common simulation logic shared across 1-Topf and 3-Topf strategies
 */

import { SteuerParameter } from '../types';

import { CacheKeys, garchSequenceCache, rateCalculationCache } from './CacheManager';
import { performanceMonitor } from './PerformanceMonitor';
import { BaseSimulationUtils } from './BaseSimulationUtils';
import { GARCHRateCalculationIntegration, GARCHUtils } from '../utils/garchModeling';
import { GARCHSequenceService } from './GARCHSequenceService';
import {
  BaseInitialValues,
  BaseSimulationOptions,
  BaseYearData,
  CrisisDefinitionType,
  CrisisState,
  ISimulationEngine,
  PortfolioStrategy,
  RateCalculationContext,
  SharedRates,
} from '../types/simulation';
import { CrisisDetectionService } from './CrisisDetectionService';

/**
 * ServiceLoader for dynamic loading of RateCalculationService
 * Enables code splitting while maintaining synchronous API
 */
class ServiceLoader {
  private static rateCalculationService: any = null;
  private static isLoading = false;

  static async ensureRateCalculationService(): Promise<any> {
    if (this.rateCalculationService) {
      return this.rateCalculationService;
    }

    if (this.isLoading) {
      // Wait for ongoing load
      while (this.isLoading) {
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      return this.rateCalculationService;
    }

    this.isLoading = true;
    try {
      const { RateCalculationService } = await import('./RateCalculationService');
      this.rateCalculationService = RateCalculationService;
      return RateCalculationService;
    } finally {
      this.isLoading = false;
    }
  }

  static getRateCalculationService(): any {
    if (!this.rateCalculationService) {
      throw new Error(
        'RateCalculationService not loaded. Call ensureRateCalculationService() first.'
      );
    }
    return this.rateCalculationService;
  }

  static hasRateCalculationService(): boolean {
    return this.rateCalculationService !== null;
  }
}

export abstract class BaseSimulationEngine<
  TOptions extends BaseSimulationOptions,
  TYearData extends BaseYearData,
  TInitialValues extends BaseInitialValues,
> implements ISimulationEngine<TYearData, TInitialValues>
{
  protected steuerParameter: SteuerParameter;
  protected options: TOptions;
  protected inflationRates: number[] = [];
  protected sharedRates?: SharedRates;

  // Enhanced caching system
  private initialValuesCache?: TInitialValues;
  private localRatesCache: Map<string, number[]> = new Map();

  // GARCH state management
  private garchInvestitionstopfRates?: number[];
  private isUsingGARCH: boolean = false;

  // Crisis detection and capital index tracking
  protected crisisDetectionService: CrisisDetectionService;
  protected kapitalIndexHistory: number[] = [];

  constructor(
    steuerParameter: SteuerParameter, 
    options: TOptions, 
    crisisDefinition: CrisisDefinitionType = 'negative-return'
  ) {
    this.steuerParameter = steuerParameter;
    this.options = options;

    // Initialize crisis detection service
    this.crisisDetectionService = new CrisisDetectionService(crisisDefinition);

    // Detect if GARCH options are being used
    this.isUsingGARCH = GARCHUtils.isGARCHOption(this.options.investmentReturnOption);
  }

  private async ensureServiceLoaded(): Promise<void> {
    if (!ServiceLoader.hasRateCalculationService()) {
      await ServiceLoader.ensureRateCalculationService();
    }
  }

  // Abstract methods to be implemented by concrete strategies
  abstract getStrategy(): PortfolioStrategy;
  abstract calculateInitialValues(): TInitialValues;
  abstract createYearData(year: number, context: RateCalculationContext): TYearData;
  abstract processYearLogic(yearData: TYearData, previousYear?: TYearData): void;

  // Public method that delegates to abstract implementation
  public getInitialValues(): TInitialValues {
    return this.calculateInitialValues();
  }

  // Template method - main simulation algorithm
  public async runSimulation(): Promise<TYearData[]> {
    // Start performance monitoring
    const simulationId = `simulation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const strategyType = this.getStrategy() === 'three-topf' ? '3-topf' : '1-topf';
    const entnahmeJahre = this.steuerParameter.berechnungBisLebensalter - this.steuerParameter.retirementAge;
    
    performanceMonitor.startMeasurement(simulationId, 'simulation', {
      strategyType,
      years: entnahmeJahre,
      capital: this.steuerParameter.vorhandenesKapital,
      retirementAge: this.steuerParameter.retirementAge,
    });

    try {
      // Ensure service is loaded before any simulation work
      await this.ensureServiceLoaded();

      const results: TYearData[] = [];

      // Initialize rates if not using shared rates
      if (!this.sharedRates) {
        await this.generateRates(entnahmeJahre);
      }

      // Calculate initial values (with memoization)
      const initialValues = this.getCachedInitialValues();

      // Create initial year (Jahr 0)
      const initialYearData = this.createInitialYear(initialValues);
      // Process the initial year logic (withdrawals, taxes, etc.)
      this.processYearLogic(initialYearData);
      results.push(initialYearData);

      // TIMING FIX: Update crisis state for initial year
      const initialInvestmentReturn = (initialYearData as any).investitionstopfRendite || initialYearData.rendite || 0;
      this.updateEndOfYearCrisisState(0, initialInvestmentReturn, 100);

      // Simulate each year
      for (let year = 1; year <= entnahmeJahre; year++) {
        const context = this.createRateCalculationContext(year);
        const yearData = this.createYearData(year, context);

        // Apply strategy-specific logic
        this.processYearLogic(yearData, results[year - 1]);

        results.push(yearData);

        // TIMING FIX: Update end-of-year crisis state for peak tracking
        // This ensures next year's crisis detection has correct information
        if (year < entnahmeJahre) {
          const previousKapitalIndex = year === 1 ? 100 : this.kapitalIndexHistory[year - 1];
          const investmentReturn = (yearData as any).investitionstopfRendite || yearData.rendite || 0;
          this.updateEndOfYearCrisisState(year, investmentReturn, previousKapitalIndex);
        }

        // Early termination if portfolio is depleted
        if (yearData.gesamtsaldo <= 0) {
          break;
        }
      }

      // End performance monitoring with actual iterations count
      performanceMonitor.endMeasurement(simulationId, { iterations: results.length });
      
      // Performance metrics are automatically tracked by PerformanceMonitor

      return results;
    } catch (error) {
      // End monitoring even if simulation fails
      performanceMonitor.endMeasurement(simulationId);
      throw error;
    }
  }

  // Alias method for backward compatibility with tests
  public async simulate(): Promise<TYearData[]> {
    return await this.runSimulation();
  }

  // Common initial value calculation logic (extracted from all engines)
  protected calculateCommonInitialValues(): {
    verbrauchstopf: number;
    zinstopf: number;
    investitionstopf: number;
  } {
    // Use Portfolio-Entnahme KPIs as calculation basis
    const portfolioEntnahme =
      (this.steuerParameter.vorhandenesKapital * this.steuerParameter.portfolioEntnahmeRate) / 100;

    // Allocate capital into the three pots
    // BUG FIX: Cap each pot so the sum of all pots is always exactly the available capital.
    // Previously only the Investitionstopf was clamped at 0, so with
    // portfolioEntnahmeRate * (1 + anzahlJahreTopf2) > 100% the pots summed to more
    // than vorhandenesKapital.
    const kapital = this.steuerParameter.vorhandenesKapital;
    const verbrauchstopfZiel = portfolioEntnahme; // 1 year of portfolio withdrawal
    const zinstopfZiel = this.steuerParameter.anzahlJahreTopf2 * portfolioEntnahme; // X years of portfolio withdrawal

    const verbrauchstopf = Math.min(verbrauchstopfZiel, kapital);
    const zinstopf = Math.min(zinstopfZiel, kapital - verbrauchstopf);
    const investitionstopf = kapital - verbrauchstopf - zinstopf;

    return { verbrauchstopf, zinstopf, investitionstopf };
  }

  // Rate generation with enhanced caching and GARCH support
  /**
   * Generate a consistent seed for reproducible GARCH sequences
   * Based on simulation parameters to ensure same results across different engines
   */
  private generateConsistentSeed(): number {
    // Create seed from simulation parameters that should be consistent across engines
    const baseString = `${this.steuerParameter.retirementAge}-${this.steuerParameter.berechnungBisLebensalter}-${this.steuerParameter.vorhandenesKapital}-${this.options.inflationOption}-${this.options.investmentReturnOption}`;
    let hash = 0;
    for (let i = 0; i < baseString.length; i++) {
      const char = baseString.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    const seed = Math.abs(hash); // Ensure positive seed
    return seed;
  }

  protected async generateRates(entnahmeJahre: number): Promise<void> {
    // Ensure service is loaded before proceeding
    await this.ensureServiceLoaded();

    const seed = this.generateConsistentSeed();
    const cacheKey = `${this.options.inflationOption}-${this.options.investmentReturnOption}-${entnahmeJahre}-${seed}`;

    // Check local cache first
    if (this.localRatesCache.has(cacheKey)) {
      this.inflationRates = this.localRatesCache.get(cacheKey)!;
      // For GARCH, also restore the investment rates if cached
      if (this.isUsingGARCH) {
        const garchCacheKey = `garch-${cacheKey}`;
        if (this.localRatesCache.has(garchCacheKey)) {
          this.garchInvestitionstopfRates = this.localRatesCache.get(garchCacheKey)!;
        }
      }
      return;
    }

    // Generate inflation rates - now year-specific for historical/statistical options
    this.inflationRates = [];
    for (let i = 0; i <= entnahmeJahre; i++) {
      // Create year-specific cache key to avoid same rate for all years
      const rateCacheKey =
        CacheKeys.rateCalculation('inflation', this.options, this.steuerParameter) + `-year-${i}`;
      let inflationRate = rateCalculationCache.get(rateCacheKey);

      if (inflationRate === undefined) {
        const rateOptions = {
          inflationOption: this.options.inflationOption,
          investmentReturnOption: this.options.investmentReturnOption,
          tagesgeldOption: ('tagesgeldOption' in this.options
            ? this.options.tagesgeldOption
            : 'parameter-rendite') as string,
          zinstopfOption: ('zinstopfOption' in this.options
            ? this.options.zinstopfOption
            : 'parameter-rendite') as string,
        };
        // Pass year parameter for deterministic historical/statistical selection
        const RateCalculationService = ServiceLoader.getRateCalculationService();
        inflationRate = RateCalculationService.calculateInflationRate(
          rateOptions,
          this.steuerParameter,
          i
        );
        if (inflationRate !== undefined) {
          rateCalculationCache.set(rateCacheKey, inflationRate);
        }
      }

      // BUG FIX: Always push a rate for EVERY year - previously the push only happened
      // on cache MISS, so a cache HIT (global rateCalculationCache) left this.inflationRates
      // empty and subsequent runs simulated with 0% inflation.
      this.inflationRates.push(inflationRate ?? 0);
    }

    // Generate GARCH investment rates if using GARCH options
    if (this.isUsingGARCH) {
      const rateOptions = {
        inflationOption: this.options.inflationOption,
        investmentReturnOption: this.options.investmentReturnOption,
        tagesgeldOption: ('tagesgeldOption' in this.options
          ? this.options.tagesgeldOption
          : 'parameter-rendite') as string,
        zinstopfOption: ('zinstopfOption' in this.options
          ? this.options.zinstopfOption
          : 'parameter-rendite') as string,
      };

      // Use centralized GARCH service
      this.garchInvestitionstopfRates = await GARCHSequenceService.generateGARCHSequence(
        rateOptions,
        entnahmeJahre + 1, // +1 to include year 0
        seed,
        'engine',
        0, // iteration 0 for individual simulation
        this.steuerParameter
      );

      // If GARCH generation failed, fall back to standard calculation
      if (!this.garchInvestitionstopfRates) {
        this.isUsingGARCH = false;
      }
    }

    // Cache the results locally
    this.localRatesCache.set(cacheKey, [...this.inflationRates]);
  }

  // Rate calculation context creation
  protected createRateCalculationContext(year: number): RateCalculationContext {
    return {
      year,
      age: this.steuerParameter.retirementAge + year,
      isSharedRates: !!this.sharedRates,
      steuerParameter: this.steuerParameter,
      options: this.options,
    };
  }

  // Get rates with shared rate support
  protected getInflationRate(year: number): number {
    if (this.sharedRates?.inflationRates) {
      return this.sharedRates.inflationRates[year] || 0;
    }
    return this.inflationRates[year] || 0;
  }

  protected getInvestitionstopfRate(year: number): number {
    // Priority 1: Use shared rates if available (for Monte Carlo simulations)
    if (this.sharedRates?.investitionstopfRates) {
      return this.sharedRates.investitionstopfRates[year] || 0;
    }

    // Priority 2: Use pre-generated GARCH rates if available and using GARCH
    if (
      this.isUsingGARCH &&
      this.garchInvestitionstopfRates &&
      year < this.garchInvestitionstopfRates.length
    ) {
      return this.garchInvestitionstopfRates[year];
    }

    // Priority 3: Fall back to standard rate calculation
    const rateOptions = {
      inflationOption: this.options.inflationOption,
      investmentReturnOption: this.options.investmentReturnOption,
      tagesgeldOption: ('tagesgeldOption' in this.options
        ? this.options.tagesgeldOption
        : 'parameter-rendite') as string,
      zinstopfOption: ('zinstopfOption' in this.options
        ? this.options.zinstopfOption
        : 'parameter-rendite') as string,
    };
    const RateCalculationService = ServiceLoader.getRateCalculationService();
    return RateCalculationService.calculateInvestitionstopfRate(
      rateOptions,
      this.steuerParameter,
      year
    );
  }

  protected getVerbrauchstopfRate(year: number): number {
    if (this.sharedRates?.verbrauchstopfRates) {
      return this.sharedRates.verbrauchstopfRates[year] || 0;
    }
    const rateOptions = {
      inflationOption: this.options.inflationOption,
      investmentReturnOption: this.options.investmentReturnOption,
      tagesgeldOption: ('tagesgeldOption' in this.options
        ? this.options.tagesgeldOption
        : 'parameter-rendite') as string,
      zinstopfOption: ('zinstopfOption' in this.options
        ? this.options.zinstopfOption
        : 'parameter-rendite') as string,
    };
    const RateCalculationService = ServiceLoader.getRateCalculationService();
    return RateCalculationService.calculateVerbrauchstopfRate(
      rateOptions,
      this.steuerParameter,
      year
    );
  }

  protected getZinstopfRate(year: number): number {
    if (this.sharedRates?.zinstopfRates) {
      return this.sharedRates.zinstopfRates[year] || 0;
    }
    const rateOptions = {
      inflationOption: this.options.inflationOption,
      investmentReturnOption: this.options.investmentReturnOption,
      tagesgeldOption: ('tagesgeldOption' in this.options
        ? this.options.tagesgeldOption
        : 'parameter-rendite') as string,
      zinstopfOption: ('zinstopfOption' in this.options
        ? this.options.zinstopfOption
        : 'parameter-rendite') as string,
    };
    const RateCalculationService = ServiceLoader.getRateCalculationService();
    return RateCalculationService.calculateZinstopfRate(rateOptions, this.steuerParameter, year);
  }

  // Create initial year data
  protected createInitialYear(initialValues: TInitialValues): TYearData {
    const context = this.createRateCalculationContext(0);
    const yearData = this.createYearData(0, context);

    // Set initial balances
    this.setInitialBalances(yearData, initialValues);

    return yearData;
  }

  // Helper method to set initial balances (strategy-specific)
  protected abstract setInitialBalances(yearData: TYearData, initialValues: TInitialValues): void;

  // Calculate current capital index
  protected calculateCurrentKapitalIndex(
    year: number,
    investitionstopfRate: number,
    previousKapitalIndex?: number
  ): number {
    // BUG FIX v2.4.9: Check if already calculated to avoid overwriting
    if (this.kapitalIndexHistory[year] !== undefined) {
      // Already calculated - return existing value
      return this.kapitalIndexHistory[year];
    }
    
    // The Kapitalindex is a cumulative end-of-year market index anchored at a
    // notional 100 before retirement: year 0 already reflects the first year's
    // return (100 * (1 + r0)), and every subsequent year compounds on top. This
    // keeps ALL yearly returns in the series (crisis-detection peak reference and
    // the kapitalIndex/rendite synchronization both rely on this invariant).
    const prevIndex = year === 0 ? 100 : (previousKapitalIndex || this.kapitalIndexHistory[year - 1] || 100);
    const currentIndex = prevIndex * (1 + investitionstopfRate / 100);

    // Store for future reference
    this.kapitalIndexHistory[year] = currentIndex;

    return currentIndex;
  }

  // CORRECTED: Separate start-of-year decisions from end-of-year outcomes
  protected getStartOfYearCrisisDecision(year: number, previousYearMarktSituation?: 'Hoch' | 'Krise'): 'Hoch' | 'Krise' {
    // For the first year, no previous information exists
    if (year === 0) {
      return 'Hoch'; // Default to no crisis for first year start
    }
    
    if (this.crisisDetectionService.getCrisisDefinition() === 'negative-return') {
      // CORRECTED: For negative-return mode, start-of-year decisions use previous year's outcome
      return previousYearMarktSituation || 'Hoch';
    } else {
      // For kapitalindex-drawdown mode, use start-of-year capital index
      const previousKapitalIndex = year === 0 ? 100 : this.kapitalIndexHistory[year - 1];
      // BUG FIX v2.4.5: Call evaluateCrisisStartOfYear and immediately capture the return value
      // This ensures we use the crisis decision at the moment of evaluation, not a potentially
      // modified state from later evaluations
      const isStartOfYearCrisis = this.crisisDetectionService.evaluateCrisisStartOfYear(year, previousKapitalIndex);
      return isStartOfYearCrisis ? 'Krise' : 'Hoch';
    }
  }

  // Market situation determination for end-of-year recording (current year outcome)
  protected getMarktSituation(investitionstopfRate: number, year?: number, previousKapitalIndex?: number): 'Hoch' | 'Krise' {
    // BUG FIX v2.4.5: Save current crisis state to avoid polluting start-of-year decisions
    // This method is called during createYearData BEFORE processYearLogic, so we need to
    // preserve the crisis state for the start-of-year decision that happens later
    const savedCrisisState = { ...this.crisisDetectionService.getCrisisState() };
    
    let marketSituation: 'Hoch' | 'Krise' = 'Hoch';
    
    // Evaluate crisis based on current year's actual performance for recording
    if (year !== undefined) {
      // BUG FIX v2.4.9: calculateCurrentKapitalIndex now checks if already calculated
      // This prevents overwriting the correct value that was already stored in createYearData
      const currentKapitalIndex = this.calculateCurrentKapitalIndex(year, investitionstopfRate, previousKapitalIndex);
      
      this.crisisDetectionService.evaluateCrisis(
        year,
        investitionstopfRate,
        previousKapitalIndex || 100,
        currentKapitalIndex
      );
      
      // BUG FIX v2.4.5: Capture market situation BEFORE restoring state
      // This ensures endOfYear reflects the actual end-of-year crisis status
      marketSituation = this.crisisDetectionService.isInCrisis() ? 'Krise' : 'Hoch';
      
      // BUG FIX v2.4.5b: Check if crisis state changed during end-of-year evaluation
      // If crisis ended (recovery) OR started, we should NOT restore the old crisis state
      // This allows legitimate crisis transitions while protecting start-of-year decisions
      const crisisStateChanged = savedCrisisState.isInCrisis !== this.crisisDetectionService.isInCrisis();
      
      // Only restore saved state if crisis state did NOT change
      // This prevents undoing legitimate crisis transitions while still protecting start-of-year decisions
      if (!crisisStateChanged) {
        this.crisisDetectionService.restoreCrisisState(savedCrisisState);
      }
    } else {
      // No year specified, restore original state
      this.crisisDetectionService.restoreCrisisState(savedCrisisState);
    }
    
    return marketSituation;
  }

  // Get current crisis state for external access
  public getCrisisState(): CrisisState {
    return this.crisisDetectionService.getCrisisState();
  }

  // Update crisis detection with end-of-year capital index for peak tracking
  protected updateEndOfYearCrisisState(year: number, investitionstopfRate: number, previousKapitalIndex?: number): void {
    const endOfYearKapitalIndex = this.calculateCurrentKapitalIndex(
      year,
      investitionstopfRate,
      previousKapitalIndex
    );
    
    // Update peak tracking for next year's crisis detection
    this.crisisDetectionService.updateEndOfYearPeak(endOfYearKapitalIndex);
  }

  // Update crisis definition (useful for testing or mode switching)
  public setCrisisDefinition(crisisDefinition: CrisisDefinitionType): void {
    this.crisisDetectionService.setCrisisDefinition(crisisDefinition);
    // Reset capital index history when changing crisis definition
    this.kapitalIndexHistory = [];
  }

  // Shared rates setter
  public setSharedRates(rates: Partial<SharedRates>): void {
    this.sharedRates = {
      inflationRates: rates.inflationRates || [],
      verbrauchstopfRates: rates.verbrauchstopfRates || [],
      zinstopfRates: rates.zinstopfRates || [],
      investitionstopfRates: rates.investitionstopfRates || [],
    };
  }

  // Memoized initial values getter - internal use
  protected getCachedInitialValues(): TInitialValues {
    if (!this.initialValuesCache) {
      this.initialValuesCache = this.calculateInitialValues();
    }
    return this.initialValuesCache;
  }

  // Clear cache (useful for tests and multiple runs)
  public clearCache(): void {
    this.initialValuesCache = undefined;
    this.localRatesCache.clear();
  }

  // Lightweight reset for Monte Carlo iterations - preserves expensive caches
  public resetForIteration(): void {
    // Only reset state that needs to change between iterations
    // Keep cached initial values and rates for performance
    this.sharedRates = undefined;
  }

  /**
   * Clears all local caches to ensure fresh rate generation for new simulations
   * This is essential for individual simulations to generate different random values
   */
  public clearLocalCaches(): void {
    this.localRatesCache.clear();
    this.initialValuesCache = undefined;
    this.inflationRates = [];
    this.garchInvestitionstopfRates = undefined;
    // Also clear the global rate calculation cache to ensure fresh values
    rateCalculationCache.clear();
    // Clear GARCH sequence cache to ensure fresh GARCH rates
    garchSequenceCache.clear();
    // Note: Master seeds are cleared in Schritt3.tsx where RateCalculationService is properly loaded
  }

  // Utility method for CUMULATIVE inflation adjustment - FIXED
  // This was previously broken: only applied single-year inflation instead of cumulative
  // Now properly calculates compound inflation from simulation start to current year
  protected adjustForInflation(baseAmount: number, currentYear: number): number {
    if (this.options.inflationOption === 'keine-inflation') {
      return baseAmount;
    }

    // Use shared rates if available (for Schritt 3 where both engines share the same rates)
    // Otherwise fall back to local inflation rates calculation
    let ratesToUse: number[];

    if (this.sharedRates?.inflationRates && this.sharedRates.inflationRates.length > 0) {
      // Use shared rates - ensure we have enough rates for the current year
      ratesToUse = this.sharedRates.inflationRates;
    } else {
      // Ensure we have inflation rates up to the current year using local calculation
      const rateOptions = {
        inflationOption: this.options.inflationOption,
        investmentReturnOption: this.options.investmentReturnOption,
        tagesgeldOption: ('tagesgeldOption' in this.options
          ? this.options.tagesgeldOption
          : 'parameter-rendite') as string,
        zinstopfOption: ('zinstopfOption' in this.options
          ? this.options.zinstopfOption
          : 'parameter-rendite') as string,
      };
      BaseSimulationUtils.ensureInflationRatesCalculated(
        this.inflationRates,
        currentYear,
        rateOptions,
        this.steuerParameter
      );
      ratesToUse = this.inflationRates;

      // Guard: Prüfe, ob die benötigten Raten verfügbar sind
      // Dieser Pfad wird aktuell nicht erreicht, da generateRates alle Jahre 0..N vorab füllt,
      // aber als Sicherheitsmaßnahme warnen, falls dennoch Raten fehlen
      if (!ratesToUse || ratesToUse.length <= currentYear) {
        console.warn(
          `[adjustForInflation] Warnung: Fehlende Inflationsrate für Jahr ${currentYear}. ` +
          `Verfügbar: ${ratesToUse?.length ?? 0} Raten. Verwende Faktor 1 (keine Anpassung).`,
          { context: 'adjustForInflation' }
        );
      }
    }

    // Calculate cumulative inflation factor from year 0 to current year
    // Example: Year 3 with 2% inflation = baseAmount * (1.02)^3 = proper purchasing power adjustment
    const cumulativeInflationFactor = BaseSimulationUtils.calculateCumulativeInflationFactor(
      ratesToUse,
      currentYear,
      this.options.inflationOption
    );

    return baseAmount * cumulativeInflationFactor;
  }

  // Utility method for investment growth
  protected applyInvestmentReturn(amount: number, returnRate: number): number {
    return amount * (1 + returnRate / 100);
  }

  // Utility method for safe subtraction (prevents negative balances in some contexts)
  protected safeSubtract(amount: number, subtraction: number): number {
    return Math.max(0, amount - subtraction);
  }

  // Performance metrics calculation (can be overridden by strategies)
  protected calculatePerformanceMetrics(results: TYearData[]): any {
    // Basic implementation - can be enhanced by individual strategies
    const finalBalance = results[results.length - 1]?.gesamtsaldo || 0;
    const initialBalance = results[0]?.gesamtsaldo || 0;
    const years = results.length - 1;

    const totalReturn = (finalBalance - initialBalance) / initialBalance;
    const annualizedReturn = Math.pow(1 + totalReturn, 1 / years) - 1;

    return {
      totalReturn,
      annualizedReturn,
      finalBalance,
      years,
    };
  }

  // GARCH-specific methods for enhanced volatility modeling

  /**
   * Returns whether the simulation engine is currently using GARCH modeling
   */
  public isUsingGARCHModeling(): boolean {
    return this.isUsingGARCH && this.garchInvestitionstopfRates !== undefined;
  }

  /**
   * Returns the pre-generated GARCH rate sequence (if available)
   * Useful for analysis and debugging
   */
  public getGARCHRateSequence(): number[] | undefined {
    return this.garchInvestitionstopfRates ? [...this.garchInvestitionstopfRates] : undefined;
  }

  /**
   * Returns GARCH model analysis information
   * Used for educational content and parameter display
   */
  public getGARCHAnalysis(): any | null {
    if (!this.isUsingGARCH) {return null;}

    try {
      return GARCHRateCalculationIntegration.analyzeIndexDataForGARCH('world');
    } catch (error) {
      // Failed to get GARCH analysis
      return null;
    }
  }

  /**
   * Forces regeneration of GARCH rates (useful for Monte Carlo iterations)
   * This ensures each iteration gets a unique GARCH sequence
   */
  public regenerateGARCHRates(entnahmeJahre: number): void {
    if (!this.isUsingGARCH) {return;}

    try {
      const rateOptions = {
        inflationOption: this.options.inflationOption,
        investmentReturnOption: this.options.investmentReturnOption,
        tagesgeldOption: ('tagesgeldOption' in this.options
          ? this.options.tagesgeldOption
          : 'parameter-rendite') as string,
        zinstopfOption: ('zinstopfOption' in this.options
          ? this.options.zinstopfOption
          : 'parameter-rendite') as string,
      };

      // Generate fresh GARCH rate sequence
      const RateCalculationService = ServiceLoader.getRateCalculationService();
      this.garchInvestitionstopfRates = RateCalculationService.generateGARCHRateSequence(
        rateOptions,
        entnahmeJahre + 1, // +1 to include year 0
        'world'
      );
    } catch (error) {
      // Failed to regenerate GARCH rates
      this.isUsingGARCH = false;
      this.garchInvestitionstopfRates = undefined;
    }
  }

  /**
   * Enhanced reset for Monte Carlo iterations that preserves GARCH capability
   * but ensures each iteration gets fresh GARCH sequences
   */
  public resetForGARCHIteration(entnahmeJahre: number): void {
    // Standard reset
    this.resetForIteration();

    // Regenerate GARCH rates for this iteration if using GARCH
    if (this.isUsingGARCH) {
      this.regenerateGARCHRates(entnahmeJahre);
    }
  }
}

// Export ServiceLoader for test initialization
export { ServiceLoader };
